import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { QrCode, Download, Printer, Save, Power, AlertCircle, ExternalLink, PauseCircle, PlayCircle, MessageSquare, Trash2, ChevronDown, Palette, Upload, X as XIcon, Sparkles } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";
import MessageColorPicker from "./MessageColorPicker";
import { Switch } from "./ui/switch";
import { QUEUE_ICON_PRESETS, getQueueIcon, isCustomIcon } from "../lib/queueIcons";

const PER_QUEUE_MESSAGE_MAX_QUEUES = 1; // spec : message personnalisé uniquement quand 1 seule file

/**
 * Per-queue settings panel shown on the merchant dashboard.
 * Lets the establishment :
 *  - Pick which queue to manage via a dropdown selector (NEW : multi-file
 *    support — the panel was previously locked on the dashboard's "active
 *    queue" tab, which made the secondary queues' QR / message invisible).
 *  - Toggle the queue open/closed (independent of the commerce-wide switch)
 *  - Edit a custom message displayed on the visitor side & public display
 *  - View / download (PNG) / print (PDF) the QR code that points to /q/{queueId}
 *
 * Backwards-compatible : when `queues` is not passed the component falls back
 * to displaying the single `queue` prop without a dropdown (legacy mode).
 */
export default function QueueSettingsPanel({ queue: queueProp, queues, activeQueueId, onActiveChange, onChange }) {
  // Multi-file mode : pick the queue from the list when available, else fall
  // back to the single legacy prop.
  const [localId, setLocalId] = useState(activeQueueId || queueProp?.id || null);
  useEffect(() => {
    if (activeQueueId && activeQueueId !== localId) setLocalId(activeQueueId);
  }, [activeQueueId]); // eslint-disable-line react-hooks/exhaustive-deps

  const queue = (queues && queues.length)
    ? (queues.find((q) => q.id === localId) || queues[0])
    : queueProp;
  const [msg, setMsg] = useState(queue?.customMessage || "");
  const [open, setOpen] = useState(queue?.isOpen !== false);
  const [saving, setSaving] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [togglingSuspend, setTogglingSuspend] = useState(false);
  const [closeConfirm, setCloseConfirm] = useState(false);
  const [clearMsgConfirm, setClearMsgConfirm] = useState(false);

  const isSuspended = !!queue?.isSuspended;
  const suspendReason = queue?.suspendReason || null;

  // Sync local editable state ONLY when the selected queue ID changes — not on
  // every polling refresh of `queue.customMessage` / `queue.isOpen`. Otherwise
  // background re-fetches would overwrite the message the merchant is currently
  // typing, identical bug to the PromptDialog reset.
  useEffect(() => {
    setMsg(queue?.customMessage || "");
    setOpen(queue?.isOpen !== false);
    if (queue?.id) {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      // public PNG endpoint, no auth required
      setQrUrl(`${apiBase}/queues/${queue.id}/qr.png`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue?.id]);

  // Mirror the open/close state when it changes server-side (e.g. from the
  // Console card or the limit-suspend mechanism) — but never touch `msg`.
  useEffect(() => {
    setOpen(queue?.isOpen !== false);
  }, [queue?.isOpen]);

  if (!queue?.id) return null;

  const toggleSuspend = async () => {
    setTogglingSuspend(true);
    try {
      const path = isSuspended ? "resume" : "suspend";
      await api.post(`/merchant/queues/${queue.id}/${path}`);
      toast.success(isSuspended ? "File reprise" : "File suspendue");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setTogglingSuspend(false);
  };

  const saveMessage = async () => {
    setSaving(true);
    try {
      await api.patch(`/merchant/queues/${queue.id}`, { customMessage: msg });
      toast.success("Message mis à jour");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const toggleBlink = async () => {
    const next = !(queue.customMessageBlink !== false);
    try {
      await api.patch(`/merchant/queues/${queue.id}`, { customMessageBlink: next });
      toast.success(next ? "Clignotement activé" : "Clignotement désactivé");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const askClearMessage = () => {
    if (!msg && !(queue.customMessage)) return;
    setClearMsgConfirm(true);
  };

  const performClearMessage = async () => {
    setClearMsgConfirm(false);
    setSaving(true);
    try {
      await api.patch(`/merchant/queues/${queue.id}`, { customMessage: "" });
      setMsg("");
      toast.success("Message effacé");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const toggleOpen = async () => {
    if (open) {
      setCloseConfirm(true);
      return;
    }
    await performToggleOpen();
  };

  const performToggleOpen = async () => {
    try {
      const url = open ? "/merchant/queue/close" : "/merchant/queue/open";
      await api.post(`${url}?queueId=${queue.id}`);
      const newOpen = !open;
      setOpen(newOpen);
      toast.success(newOpen ? "File ouverte" : "File fermée");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const confirmClose = async () => {
    setCloseConfirm(false);
    await performToggleOpen();
  };

  const downloadPng = async () => {
    try {
      const res = await fetch(qrUrl);
      if (!res.ok) throw new Error("dl");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `BypassGo-QR-${queue.name.replace(/[^a-z0-9]+/gi, "_")}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Téléchargement impossible"); }
  };

  const printPdf = async () => {
    try {
      const token = localStorage.getItem("bypassgo_token") || localStorage.getItem("bypassgo_commerce_token");
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      const res = await fetch(`${apiBase}/merchant/queues/${queue.id}/qr.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("dl");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `BypassGo-QR-${queue.name.replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Génération PDF impossible"); }
  };

  const publicLandingUrl = `${window.location.origin}/q/${queue.id}`;

  return (
    <div className="mt-4 sm:mt-6 rounded-2xl border border-border bg-white shadow-sm overflow-hidden" data-testid="queue-settings-panel">
      <div className="px-4 sm:px-5 py-4 border-b border-border bg-surface/60 flex flex-col gap-3" data-testid="queue-settings-header">
        {/* Title + queue picker — stacked block, premium-styled dropdown. */}
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-[11px] font-black uppercase tracking-[0.14em] text-subtle shrink-0">Paramètres de la file</p>
          {(queues && queues.length > 1) ? (
            <div className="relative inline-block">
              <select
                value={queue?.id || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  setLocalId(v);
                  onActiveChange && onActiveChange(v);
                }}
                className="appearance-none pl-3 pr-9 h-10 rounded-lg border border-border bg-white text-ink font-display font-bold text-sm shadow-sm hover:border-ink focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand cursor-pointer transition-colors"
                data-testid="queue-settings-selector"
                aria-label="Choisir la file à paramétrer"
              >
                {queues.map((q) => (
                  <option key={q.id} value={q.id}>{q.name}</option>
                ))}
              </select>
              <span
                aria-hidden="true"
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-5 h-5 rounded-md bg-ink/5 text-ink pointer-events-none"
              >
                <ChevronDown size={12} strokeWidth={3} />
              </span>
            </div>
          ) : (
            <span className="inline-flex items-center h-10 px-3 rounded-lg border border-border bg-white text-ink font-display font-bold text-sm shadow-sm" data-testid="queue-settings-name">
              {queue?.name}
            </span>
          )}
        </div>

      </div>

      <div className="grid grid-cols-1 gap-0">
        {/* APPEARANCE — color + icon (preset OR custom upload) */}
        <div className="p-4 sm:p-5 border-b border-border" data-testid="queue-appearance-block">
          <QueueAppearanceEditor queue={queue} onChange={onChange} />
        </div>

        {/* Spec utilisateur fork 25.35 — bloc « QR CODE DE LA FILE »
           supprimé du panneau de paramétrage pour tous les comptes
           (existants et futurs). La fonctionnalité backend
           (`queue-qr-image`, /api/qr/...) reste accessible via les
           routes directes pour usages internes ; seule l'UI est
           masquée. */}

        {/* Custom message editor. Disabled (hidden) when the
           establishment has 4 or more queues per spec. */}
        {(queues?.length || 1) <= PER_QUEUE_MESSAGE_MAX_QUEUES ? (
          <div className="p-4 sm:p-5">
            <h3 className="text-sm font-black uppercase tracking-[0.08em] text-ink inline-flex items-center gap-2">
              <MessageSquare size={16} className="text-brand" /> Message personnalisé dans la file
            </h3>
            <p className="text-xs text-subtle mt-2 leading-relaxed">
              Affiché côté visiteur (page de prise de ticket) et sur l'écran public, dans la file. 100 caractères max (espaces non comptés).
              <br /><span className="text-[11px] italic">Cette option est disponible uniquement si vous avez <strong>une seule</strong> file d'attente.</span>
            </p>
            {/* Blink toggle — switch slides right/left to enable/disable the 1s
                blinking animation of the message on the public display. */}
            <div className="mt-3 flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-surface border border-border" data-testid="queue-custom-message-blink-row">
              <div className="min-w-0">
                <p className="text-xs font-bold uppercase tracking-wider text-ink">Clignotement (1s)</p>
                <p className="text-[11px] text-subtle leading-snug mt-0.5">Le message clignote toutes les secondes pour attirer l'attention.</p>
              </div>
              <Switch
                checked={queue.customMessageBlink !== false}
                onCheckedChange={toggleBlink}
                data-testid="queue-custom-message-blink-toggle"
                aria-label="Activer ou désactiver le clignotement du message"
              />
            </div>
            {/* Spec utilisateur fork 25.42 — sélecteur de couleur pour le
               message personnalisé (4 choix : bleu / rouge / noir / vert). */}
            <MessageColorPicker
              value={queue.customMessageColor || "blue"}
              onChange={async (next) => {
                try {
                  await api.patch(`/merchant/queues/${queue.id}`, { customMessageColor: next });
                  toast.success("Couleur du message mise à jour");
                  onChange && onChange();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Erreur");
                }
              }}
              testId="queue-custom-message-color"
            />
            <textarea
              value={msg}
              onChange={(e) => {
                // Spec utilisateur fork 25.42 — limite abaissée à 100
                // caractères hors-espaces (cohérent avec le backend).
                const upper = e.target.value.toUpperCase();
                let count = 0;
                let cut = upper.length;
                for (let i = 0; i < upper.length; i++) {
                  if (upper[i] !== " ") count++;
                  if (count > 100) { cut = i; break; }
                }
                setMsg(upper.slice(0, cut));
              }}
              rows={5}
              placeholder='Ex : "La file ouvre dans 10 minutes" · "Fermeture entre 12h et 14h" · "Nous sommes absents"'
              className="mt-3 w-full border border-border p-3 text-sm font-mono rounded-md focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand"
              data-testid="queue-custom-message-input"
            />
            <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
              <span className="text-xs font-mono text-subtle" data-testid="queue-custom-message-counter">
                {msg.replace(/\s/g, "").length} / 100
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={askClearMessage}
                  disabled={saving || (!msg && !queue.customMessage)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-destructive/40 text-destructive hover:bg-destructive hover:text-white text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                  data-testid="queue-custom-message-clear-btn"
                >
                  <Trash2 size={12} /> Effacer
                </button>
                <button
                  onClick={saveMessage}
                  disabled={saving || msg === (queue.customMessage || "")}
                  className="inline-flex items-center gap-1.5 h-9 px-4 bg-brand text-white text-xs font-bold uppercase tracking-wider rounded-md disabled:opacity-50 hover:bg-brand-hover"
                  data-testid="queue-custom-message-save-btn"
                >
                  <Save size={12} /> {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 sm:p-5 bg-surface/40 text-center" data-testid="queue-custom-message-disabled-note">
            <MessageSquare size={18} className="mx-auto text-subtle mb-2" />
            <p className="text-xs text-subtle leading-relaxed">
              <span className="font-bold text-ink">Message personnalisé indisponible.</span>
              <br />Cette option est réservée aux Établissements n'ayant qu'<strong>une seule file d'attente</strong>.
              Avec {queues?.length || 0} files, utilisez le « <span className="font-bold text-ink">Message Établissement global</span> » du tableau de bord.
            </p>
          </div>
        )}
      </div>

      {!open && !isSuspended && (
        <div className="border-t border-border bg-destructive/5 px-4 py-2 text-xs text-destructive flex items-center gap-1.5" data-testid="queue-closed-warning">
          <AlertCircle size={12} /> Cette file est fermée. Les visiteurs verront « Cette file est actuellement fermée » et ne pourront pas prendre de ticket.
        </div>
      )}
      {isSuspended && (
        <div className="border-t border-border bg-[#F18F18]/10 px-4 py-2 text-xs text-[#A85C00] flex items-center gap-1.5 font-bold" data-testid="queue-suspended-warning">
          <PauseCircle size={12} /> File suspendue {suspendReason === "limit" ? "automatiquement (limite atteinte)" : "manuellement"}. Aucun nouveau ticket ne peut être pris ; les numéros sont figés. Cliquez sur « Reprendre la file » pour relancer.
        </div>
      )}

      <ConfirmDialog
        open={closeConfirm}
        title="Fermer cette file d'attente ?"
        message={
          <>
            <p>Cette action <strong>réinitialisera automatiquement le compteur à zéro</strong>. Les tickets déjà attribués ne seront plus valables.</p>
            <p className="text-destructive font-bold">Cette opération est irréversible.</p>
          </>
        }
        confirmLabel="OK"
        destructive
        onConfirm={confirmClose}
        onCancel={() => setCloseConfirm(false)}
        testid="queue-panel-close-confirm"
      />

      <ConfirmDialog
        open={clearMsgConfirm}
        title="Supprimer ce message personnalisé ?"
        message="Le message disparaîtra immédiatement de l'écran public et des vues visiteur."
        confirmLabel="OK"
        destructive
        onConfirm={performClearMessage}
        onCancel={() => setClearMsgConfirm(false)}
        testid="queue-custom-message-clear-confirm"
      />
    </div>
  );
}



/**
 * Per-queue appearance editor : color swatches + preset icon picker + custom
 * icon upload (PNG / JPG / SVG, auto-resized client-side to 128 px). All
 * fields optional ; an empty selection clears the customisation.
 */
function QueueAppearanceEditor({ queue, onChange }) {
  const [icon, setIcon] = useState(queue?.icon || "");
  const [saving, setSaving] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [iconPickerOpen, setIconPickerOpen] = useState(false);

  useEffect(() => {
    setIcon(queue?.icon || "");
  }, [queue?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const PresetIcon = getQueueIcon(icon);
  const customDataUrl = isCustomIcon(icon) ? icon : null;

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.patch(`/merchant/queues/${queue.id}`, patch);
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const handlePresetIcon = async (k) => {
    const next = icon === k ? "" : k;
    setIcon(next);
    await save({ icon: next });
  };

  const clearIcon = async () => {
    setIcon("");
    await save({ icon: "" });
  };

  const handleUpload = (e) => {
    setUploadErr("");
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-uploading the same file
    if (!file) return;
    if (!/^image\/(png|jpe?g|svg\+xml)$/.test(file.type)) {
      setUploadErr("Format non supporté (PNG, JPG ou SVG uniquement)");
      return;
    }
    if (file.size > 1_500_000) {
      setUploadErr("Image trop volumineuse (1,5 Mo max)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const raw = String(reader.result || "");
      try {
        if (file.type === "image/svg+xml") {
          // SVG : keep as-is (already vector / lightweight)
          setIcon(raw);
          await save({ icon: raw });
          return;
        }
        // PNG / JPG : downscale to 128 px square on a canvas to keep payloads small
        const img = new Image();
        img.onload = async () => {
          const TARGET = 128;
          const canvas = document.createElement("canvas");
          canvas.width = TARGET;
          canvas.height = TARGET;
          const ctx = canvas.getContext("2d");
          // Preserve aspect ratio inside a TARGETxTARGET box
          const ratio = Math.min(TARGET / img.width, TARGET / img.height);
          const w = img.width * ratio;
          const h = img.height * ratio;
          const x = (TARGET - w) / 2;
          const y = (TARGET - h) / 2;
          ctx.clearRect(0, 0, TARGET, TARGET);
          ctx.drawImage(img, x, y, w, h);
          const out = canvas.toDataURL("image/png", 0.9);
          setIcon(out);
          await save({ icon: out });
        };
        img.onerror = () => setUploadErr("Image illisible");
        img.src = raw;
      } catch {
        setUploadErr("Erreur d'import");
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid="queue-appearance-editor">
      <h3 className="text-sm font-black uppercase tracking-[0.08em] text-ink inline-flex items-center gap-2 flex-wrap">
        <Palette size={16} className="text-brand" /> Apparence de la file
        {queue?.letter && (
          <span className="ml-2 inline-flex items-center justify-center px-2 h-6 rounded bg-ink text-white font-mono text-xs">
            Lettre {queue.letter}
          </span>
        )}
        {queue?.color && (
          <span
            className="inline-flex items-center gap-1.5 px-2 h-6 rounded text-white font-mono text-[10px] uppercase tracking-wider"
            style={{ backgroundColor: queue.color }}
            data-testid="queue-color-pill"
            title="Couleur attribuée automatiquement par l'application"
          >
            Couleur auto
          </span>
        )}
      </h3>
      <p className="text-xs text-subtle mt-2 leading-relaxed">
        La couleur est attribuée automatiquement par BypassGo et reste stable.
        Vous pouvez en revanche choisir une icône pour personnaliser l'affichage.
      </p>

      <div className="mt-5">
        <p className="text-[11px] font-bold uppercase tracking-wider text-subtle mb-2">
          Icône de la file (facultatif)
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {/* Single shortcut to open the icon picker — same UX as the
              Establishment icon shortcut at the top of the dashboard. */}
          <button
            type="button"
            onClick={() => setIconPickerOpen(true)}
            disabled={saving}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-border text-subtle hover:border-ink hover:text-ink transition-colors"
            data-testid="queue-icon-shortcut-btn"
            title="Choisir l'icône de la file"
          >
            <Sparkles size={16} className="text-brand" />
            <span className="text-[11px] font-bold uppercase tracking-wider">Icône</span>
          </button>
          {/* Active icon preview to give immediate feedback. */}
          {customDataUrl ? (
            <div className="inline-flex items-center gap-2" data-testid="queue-icon-active-preview">
              <img src={customDataUrl} alt="" className="w-9 h-9 rounded border border-border bg-white object-contain" />
              <button
                type="button"
                onClick={clearIcon}
                className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-border text-subtle hover:border-ink hover:text-ink text-[11px] uppercase font-bold"
                data-testid="queue-icon-clear"
              >
                <XIcon size={12} /> Retirer
              </button>
            </div>
          ) : PresetIcon ? (
            <div className="inline-flex items-center gap-1.5 text-xs text-subtle" data-testid="queue-icon-active-preview">
              <PresetIcon size={16} style={{ color: queue?.color || undefined }} /> Icône active
              <button
                type="button"
                onClick={clearIcon}
                className="ml-2 inline-flex items-center gap-1 h-8 px-2 rounded-md border border-border text-subtle hover:border-ink hover:text-ink text-[11px] uppercase font-bold"
                data-testid="queue-icon-clear"
              >
                <XIcon size={12} /> Retirer
              </button>
            </div>
          ) : (
            <span className="text-xs text-subtle">Aucune icône — cliquez sur « Icône » pour en choisir une.</span>
          )}
        </div>
        {uploadErr && (
          <p className="mt-2 text-xs text-destructive" data-testid="queue-icon-upload-error">{uploadErr}</p>
        )}
      </div>

      {/* ICON PICKER MODAL — opens from the shortcut button above. Mirrors
         the establishment icon picker (20 presets + custom upload). */}
      {iconPickerOpen && (
        <div
          className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIconPickerOpen(false)}
          data-testid="queue-icon-picker-modal"
        >
          <div
            className="relative w-full max-w-3xl bg-white rounded-2xl border border-border shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-5 py-3 border-b border-border flex items-center justify-between">
              <h2 className="font-display font-black uppercase tracking-[0.06em] text-base text-ink inline-flex items-center gap-2">
                <Sparkles size={16} className="text-brand" /> Icône de la file
              </h2>
              <button
                onClick={() => setIconPickerOpen(false)}
                className="text-subtle hover:text-ink"
                aria-label="Fermer"
                data-testid="queue-icon-picker-close"
              >
                <XIcon size={18} />
              </button>
            </header>
            <div className="p-5 max-h-[75vh] overflow-y-auto">
              <p className="text-xs text-subtle leading-relaxed">
                Choisissez une icône qui représente cette file (caisse, accueil, expertise…). Elle s'affichera sur l'écran public.
              </p>
              <div className="mt-4 grid gap-2 sm:gap-2.5 grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-9 xl:grid-cols-10" data-testid="queue-icon-presets">
                {QUEUE_ICON_PRESETS.map(({ key, label, Icon }) => {
                  const active = icon === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => { handlePresetIcon(key); setIconPickerOpen(false); }}
                      disabled={saving}
                      title={label}
                      className={`group flex flex-col items-center justify-start gap-1.5 p-2 rounded-xl border transition-colors min-h-[88px] ${active ? "border-brand bg-brand/10 text-brand ring-2 ring-brand/30" : "border-[#E1E8F5] bg-[#F2F6FF] hover:border-brand hover:bg-white"}`}
                      data-testid={`queue-icon-${key}`}
                    >
                      <span className="flex items-center justify-center w-10 h-10 shrink-0">
                        <Icon size={28} strokeWidth={1.8} className={active ? "text-brand" : "text-ink"} />
                      </span>
                      <span className={`text-[10px] leading-tight text-center font-bold ${active ? "text-brand" : "text-ink"}`}>
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>

              <div className="mt-5 pt-5 border-t border-border flex flex-wrap items-center gap-3">
                <label
                  className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border-2 border-border hover:border-ink text-ink text-xs font-bold uppercase tracking-wider cursor-pointer"
                  data-testid="queue-icon-upload-label"
                >
                  <Upload size={13} /> Importer une icône
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={(e) => { handleUpload(e); setIconPickerOpen(false); }}
                    className="sr-only"
                    disabled={saving}
                    data-testid="queue-icon-upload-input"
                  />
                </label>
                <p className="text-[11px] text-subtle">
                  PNG · JPG · SVG, 1,5 Mo maximum. L'icône sera redimensionnée automatiquement.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
